export interface FirebaseConfigEnv {
  REACT_APP_FIREBASE_API_KEY?: string;
  REACT_APP_FIREBASE_AUTH_DOMAIN?: string;
  REACT_APP_FIREBASE_PROJECT_ID?: string;
  REACT_APP_FIREBASE_STORAGE_BUCKET?: string;
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID?: string;
  REACT_APP_FIREBASE_APP_ID?: string;
  [key: string]: string | undefined;
}

export interface FirebaseClientConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

const REQUIRED_PUBLIC_CONFIG_FIELDS: Array<keyof FirebaseClientConfig> = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
];

/**
 * Fail closed for configured production/preview builds while keeping unit
 * tests and the emulator's intentionally sparse defaults usable.
 */
export function validatePublicFirebaseConfig(
  config: FirebaseClientConfig,
  options: { environment?: string; required?: boolean } = {},
): FirebaseClientConfig {
  const required = options.required ?? false;
  if (!required) return config;
  const missing = REQUIRED_PUBLIC_CONFIG_FIELDS.filter((field) => {
    const value = config[field];
    return typeof value !== 'string' || value.trim().length === 0;
  });
  if (missing.length > 0) {
    const environment = options.environment || 'configured';
    throw new Error(
      `Firebase public configuration is incomplete for ${environment}: ${missing.join(', ')}`,
    );
  }
  return config;
}

function defaultAuthDomain(projectId?: string): string | undefined {
  return projectId ? `${projectId}.firebaseapp.com` : undefined;
}

const CANONICAL_AUTH_DOMAINS: Record<string, string> = {
  'workbench-688ed': 'workbench.alignedhq.ai',
};

export function resolveAuthDomain(
  configuredAuthDomain: string | undefined,
  projectId: string | undefined,
  hostname: string | undefined,
): string | undefined {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return configuredAuthDomain ?? defaultAuthDomain(projectId);
  }

  if (projectId && CANONICAL_AUTH_DOMAINS[projectId]) {
    return CANONICAL_AUTH_DOMAINS[projectId];
  }

  return configuredAuthDomain ?? defaultAuthDomain(projectId);
}

export function buildFirebaseConfig(
  env: FirebaseConfigEnv,
  hostname: string | undefined,
): FirebaseClientConfig {
  const config: FirebaseClientConfig = {
    apiKey: env.REACT_APP_FIREBASE_API_KEY,
    authDomain: resolveAuthDomain(
      env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      env.REACT_APP_FIREBASE_PROJECT_ID,
      hostname,
    ),
    projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.REACT_APP_FIREBASE_APP_ID,
  };
  const environment = env.REACT_APP_ENVIRONMENT || env.WORKBENCH_ENVIRONMENT;
  const required = env.REACT_APP_CONFIG_REQUIRED === 'true' ||
    ['production', 'preview', 'staging'].includes(environment || '');
  return validatePublicFirebaseConfig(config, { environment, required });
}
